// Klubbordre fra nettbutikken (klubbsport1.no og nettbutikken, POST /klubbordre), nederst på
// Trykklisten (ServiceList.js), i startsidens stil. Viser ordrer for klubber som hører til butikken
// den ansatte er i (og klubber uten butikk). Raden åpner ordren (/ordre/:orderId).
import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { Valgknapper, feltKlasse } from './ny-kundesamhandling/Skjema';
import Ikon from './ny-kundesamhandling/Ikon';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Nye klubbordre får status «Mottatt» (ClubOrder), og de ansatte setter «Aktiv» osv. på ordren.
// «Åpne» viser begge (før 28.10 manglet «Mottatt» i filteret, så nye ordrer syntes bare under «Alle»).
const APNE = ['Mottatt', 'Aktiv', ''];
const STATUSER = [
  { verdi: 'Apne', tittel: 'Åpne' },
  { verdi: 'Sendt', tittel: 'Sendt' },
  { verdi: 'Avsluttet', tittel: 'Avsluttet' },
  { verdi: 'Kansellert', tittel: 'Kansellert' },
  { verdi: 'Alle', tittel: 'Alle' },
];
const BUTIKK_ID = { ROYKEN: '1', SLEMMESTAD: '2' };
const BUTIKK_NAVN = { ROYKEN: 'Røyken', SLEMMESTAD: 'Slemmestad' };

const KOLONNER = [
  { key: 'navn', tittel: 'Kunde' },
  { key: 'antall', tittel: 'Varer' },
  { key: 'belop', tittel: 'Beløp', bred: true },
  { key: 'status', tittel: 'Status' },
  { key: 'dato', tittel: 'Bestilt', bred: true },
];

const kr = (n) => `${new Intl.NumberFormat('nb-NO').format(Math.round(Number(n) || 0))} kr`;
const datoTekst = (d) => (d ? new Date(d).toLocaleString('nb-NO', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).replace(',', '') : '');

function StatusMerke({ status }) {
  const cls = status === 'Avsluttet' || status === 'Kansellert' ? 'bg-slate-200 text-slate-800'
    : status === 'Sendt' ? 'bg-sky-100 text-sky-900' : status === 'Mottatt' || !status ? 'bg-amber-100 text-amber-950' : 'bg-emerald-100 text-emerald-900';
  return <span className={`inline-block whitespace-nowrap rounded-full px-2.5 py-0.5 text-sm font-semibold ${cls}`}>{status || 'Mottatt'}</span>;
}

function ClubNet() {
  const [ordrer, setOrdrer] = useState([]);
  const [lastet, setLastet] = useState(false);
  const [feil, setFeil] = useState('');
  const [sok, setSok] = useState('');
  const [status, setStatus] = useState('Apne');
  const [sortConfig, setSortConfig] = useState({ key: 'dato', direction: 'descending' });
  const navigate = useNavigate();
  const butikkid = Cookies.get('butikkid');

  useEffect(() => {
    const hent = async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/cluborders`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        setOrdrer(data
          .filter((o) => Array.isArray(o.items) && o.items.length > 0)
          .map((o) => {
            const tilhorighet = o.customerId?.butikktilhorighet || '';
            return {
              id: o._id,
              navn: [o.customerId?.firstName, o.customerId?.lastName].filter(Boolean).join(' ') || 'Ukjent',
              tilhorighet,
              butikkIdKlubb: BUTIKK_ID[tilhorighet] || null,
              antall: o.items.length,
              belop: o.totalAmount,
              status: o.status || '',
              dato: o.orderDate ? new Date(o.orderDate) : null,
            };
          }));
        setFeil('');
      } catch (e) {
        console.error('Feil ved henting av klubbordre:', e.message);
        setFeil('Klarte ikke å hente klubbordrene. Last siden på nytt om litt.');
      } finally {
        setLastet(true);
      }
    };
    hent();
  }, []);

  const liste = useMemo(() => {
    const q = sok.trim().toLowerCase();
    const utvalg = ordrer
      // Klubber som hører til butikken den ansatte er i, og klubber uten butikk.
      .filter((o) => o.butikkIdKlubb === null || o.butikkIdKlubb === butikkid)
      .filter((o) => status === 'Alle' || (status === 'Apne' ? APNE.includes(o.status) : o.status === status))
      .filter((o) => !q || [o.navn, o.status, BUTIKK_NAVN[o.tilhorighet], datoTekst(o.dato)].join(' ').toLowerCase().includes(q));
    const { key, direction } = sortConfig;
    const r = direction === 'ascending' ? 1 : -1;
    return [...utvalg].sort((a, b) => {
      if (key === 'antall' || key === 'belop') return r * ((Number(a[key]) || 0) - (Number(b[key]) || 0));
      if (key === 'dato') return r * ((a.dato || 0) - (b.dato || 0));
      return r * String(a[key] || '').localeCompare(String(b[key] || ''), 'nb');
    });
  }, [ordrer, sok, status, sortConfig, butikkid]);

  const sorterPa = (key) => setSortConfig((c) => ({ key, direction: c.key === key && c.direction === 'ascending' ? 'descending' : 'ascending' }));

  return (
    <section aria-labelledby="kn-tittel" className="space-y-3">
      <h2 id="kn-tittel" className="text-lg font-bold text-slate-900">Klubbordre fra nettbutikken</h2>

      <div className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-3">
        <div>
          <label htmlFor="kn-sok" className="mb-1 block font-semibold text-slate-800">Søk i klubbordrene</label>
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"><Ikon navn="sok" className="w-6 h-6" /></span>
            <input id="kn-sok" type="search" inputMode="search" autoComplete="off" value={sok} onChange={(e) => setSok(e.target.value)}
              placeholder="Navn, status, butikk eller dato" aria-describedby="kn-sok-status" className={`${feltKlasse} pl-12`} />
          </div>
          <p id="kn-sok-status" role="status" className="mt-1.5 min-h-[1.25rem] text-sm text-slate-600">
            {sok.trim() && lastet ? `${liste.length} ${liste.length === 1 ? 'klubbordre' : 'klubbordrer'} passer` : ''}
          </p>
        </div>
        <Valgknapper id="kn-status" etikett="Status" valg={STATUSER} verdi={status} onVelg={setStatus} />
      </div>

      {feil && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-1.5 text-red-800">{feil}</p>}

      {!lastet ? (
        <p className="rounded-xl border border-dashed border-slate-300 px-3 py-5 text-slate-700">Henter klubbordre …</p>
      ) : liste.length === 0 ? (
        <p className="rounded-xl border border-dashed border-slate-300 px-3 py-5 text-slate-700">
          {sok.trim() ? 'Ingen klubbordre passer søket.' : 'Ingen klubbordre med denne statusen.'}
        </p>
      ) : (
        <div tabIndex={0} role="region" aria-label="Klubbordre, kan rulles sidelengs"
          className="overflow-x-auto rounded-2xl border border-slate-200 bg-white focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50">
          <table className="min-w-full text-left">
            <caption className="sr-only">Klubbordre. Trykk på en kolonneoverskrift for å sortere.</caption>
            <thead className="bg-slate-100 text-sm font-semibold text-slate-700">
              <tr>
                {KOLONNER.map((k) => (
                  <th key={k.key} scope="col" aria-sort={sortConfig.key === k.key ? sortConfig.direction : undefined}
                    className={`px-2 py-1.5 ${k.bred ? 'hidden md:table-cell' : ''}`}>
                    <button type="button" onClick={() => sorterPa(k.key)}
                      className="inline-flex min-h-[2.5rem] items-center gap-1 rounded-lg px-2 hover:bg-slate-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600">
                      {k.tittel}
                      {sortConfig.key === k.key && <Ikon navn="ned" className={`w-4 h-4 ${sortConfig.direction === 'ascending' ? 'rotate-180' : ''}`} />}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 text-slate-800">
              {liste.map((o) => (
                <tr key={o.id} className="cursor-pointer align-top hover:bg-violet-50/50" onClick={() => navigate(`/ordre/${o.id}`)}>
                  <td className="min-w-[10rem] px-3 py-1.5">
                    <Link to={`/ordre/${o.id}`} onClick={(e) => e.stopPropagation()}
                      className="font-semibold text-indigo-700 underline underline-offset-2 hover:text-indigo-900 [overflow-wrap:anywhere]">
                      {o.navn}
                    </Link>
                    {o.tilhorighet && <span className="block text-sm text-slate-600">{BUTIKK_NAVN[o.tilhorighet] || o.tilhorighet}</span>}
                    <span className="block text-sm text-slate-600 md:hidden">{[kr(o.belop), datoTekst(o.dato)].join(' · ')}</span>
                  </td>
                  <td className="px-3 py-1.5">{o.antall}</td>
                  <td className="hidden whitespace-nowrap px-3 py-1.5 md:table-cell">{kr(o.belop)}</td>
                  <td className="px-3 py-1.5"><StatusMerke status={o.status} /></td>
                  <td className="hidden whitespace-nowrap px-3 py-1.5 text-sm md:table-cell">{datoTekst(o.dato)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default ClubNet;
